// Lissage des bords SANS vectoriser (Daniel 15.10.2026) : après un retrait de couleurs, le contour garde un escalier et de
// petits points. On travaille sur le canal alpha seulement : médiane 3×3 (tue les marches d'un pixel et le grain), puis léger
// flou gaussien (σ ≈ 1) et remise en forme douce (smoothstep 64→192) qui redonne un bord net mais anti-crénelé. Les couleurs
// ne bougent pas. 100 % navigateur, gratuit, ~0,2 s pour 4 Mpx. Les traits très fins (< 2 px) peuvent s'affiner : outil à
// proposer, jamais imposé.
const loadImage = (src: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const im = new Image();
		im.onload = () => resolve(im);
		im.onerror = () => reject(new Error('image illisible'));
		im.src = src;
	});

function median3(a: Uint8ClampedArray, w: number, h: number): Uint8ClampedArray {
	const out = new Uint8ClampedArray(a.length);
	const win = new Uint8Array(9);
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			let n = 0;
			for (let dy = -1; dy <= 1; dy++) {
				const yy = Math.min(h - 1, Math.max(0, y + dy));
				for (let dx = -1; dx <= 1; dx++) {
					const xx = Math.min(w - 1, Math.max(0, x + dx));
					win[n++] = a[yy * w + xx];
				}
			}
			// médiane de 9 valeurs : tri par insertion (petit tableau)
			for (let i = 1; i < 9; i++) {
				const v = win[i];
				let j = i - 1;
				while (j >= 0 && win[j] > v) {
					win[j + 1] = win[j];
					j--;
				}
				win[j + 1] = v;
			}
			out[y * w + x] = win[4];
		}
	}
	return out;
}

function blurSep(a: Uint8ClampedArray, w: number, h: number): Float32Array {
	// noyau gaussien σ≈1 : [1 4 6 4 1] / 16
	const k = [1 / 16, 4 / 16, 6 / 16, 4 / 16, 1 / 16];
	const tmp = new Float32Array(a.length);
	const out = new Float32Array(a.length);
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			let s = 0;
			for (let i = -2; i <= 2; i++) s += k[i + 2] * a[y * w + Math.min(w - 1, Math.max(0, x + i))];
			tmp[y * w + x] = s;
		}
	}
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			let s = 0;
			for (let i = -2; i <= 2; i++) s += k[i + 2] * tmp[Math.min(h - 1, Math.max(0, y + i)) * w + x];
			out[y * w + x] = s;
		}
	}
	return out;
}

export interface SmoothResult {
	src: string;
	aspect: number;
	/** part des pixels de bord modifiés (information) */
	changedPct: number;
}

export async function smoothEdges(src: string): Promise<SmoothResult | null> {
	const im = await loadImage(src);
	const w = im.naturalWidth;
	const h = im.naturalHeight;
	if (!w || !h) return null;
	const c = document.createElement('canvas');
	c.width = w;
	c.height = h;
	const ctx = c.getContext('2d', { willReadFrequently: true });
	if (!ctx) return null;
	ctx.drawImage(im, 0, 0);
	const img = ctx.getImageData(0, 0, w, h);
	const d = img.data;
	const n = w * h;
	const alpha = new Uint8ClampedArray(n);
	let transparent = false;
	for (let i = 0; i < n; i++) {
		alpha[i] = d[i * 4 + 3];
		if (alpha[i] < 255) transparent = true;
	}
	// image entièrement opaque (pas de fond retiré) : rien à lisser
	if (!transparent) return null;
	const med = median3(alpha, w, h);
	const bl = blurSep(med, w, h);
	let changed = 0;
	for (let i = 0; i < n; i++) {
		// smoothstep entre 64 et 192 : bord net, anti-crénelé sur ~1 px
		const t = Math.min(1, Math.max(0, (bl[i] - 64) / 128));
		const v = Math.round(255 * t * t * (3 - 2 * t));
		if (Math.abs(v - alpha[i]) > 8) changed++;
		d[i * 4 + 3] = v;
	}
	ctx.putImageData(img, 0, 0);
	return { src: c.toDataURL('image/png'), aspect: w / h, changedPct: Math.round((changed / n) * 10000) / 100 };
}
